import { Warning } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import { TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ReactComponent from '@/components/ReactComponent';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import { MEIO_JULGAMENTO_SESSAO } from '@/dominio/MeioJulgamentoSessao';
import TipoRemessaJulgamentoProcesso, {
    TIPO_REMESSA_JULGAMENTO_PROCESSO
} from '@/dominio/TipoRemessaJulgamentoProcesso';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { MENSAGEM_INCOSISTENCIA } from './IconeTipoJulgamento';
dayjs.extend(customParseFormat);

export const QTD_MAXIMA_CARACTERES_MOTIVO_REMESSA = 200;
const MOTIVO_PADRAO = 'Solicitação do(a) secretário(a)';

class IconeTipoRemessaJulgamentoFormRemessa extends ReactComponent {
    /** @type {string | undefined} */
    novoTipoRemessa = undefined;

    /** @type {string | undefined} */
    novoMotivoRemessa = undefined;

    /** @type {string | undefined} */
    novoComplementoMotivoRemessa = undefined;

    /** @type {boolean} */
    openConfirmacaoExclusaoRemessa = false;

    /** @type {boolean} */
    exclusaoConfirmada = false;

    /** @type {boolean} */
    executandoAcao = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            novoTipoRemessa: observable,
            novoMotivoRemessa: observable,
            novoComplementoMotivoRemessa: observable,
            openConfirmacaoExclusaoRemessa: observable,
            exclusaoConfirmada: observable,
            executandoAcao: observable,
            registro: computed,
            idTipoRemessaJulgamento: computed,
            motivoRemessaJulgamento: computed,
            complementoMotivoRemessaJulgamento: computed,
            sessaoSemPeriodoVirtualDefinido: computed,
            sessaoIdAssociada: computed,
            sessaoSelecionada: computed,
            meioJulgamentoSessao: computed,
            pautaExclusivamenteVirtual: computed,
            isNovoMotivoPreenchido: computed,
            isNovoComplementoMotivoPreenchido: computed,
            isDadosNovosIguaisAosAntigos: computed,
            isCamposObrigatoriosPreenchidos: computed,
            isCamposConsistentes: computed,
            msgDadosObrigatorios: computed,
            msgDadosInconsistentes: computed,
            dicaBotaoGravar: computed,
            isFormOk: computed,
            handleChangeTipoRemessaJulgamento: action,
            handleChangeMotivoRemessaJulgamento: action,
            handleChangeComplementoMotivoRemessaJulgamento: action,
            definirMotivoPadrao: action,
            handleCloseConfirmacaoExclusao: action,
            handleClickConfirmacaoExclusao: action,
            salvarRemessa: action,
            handleCloseMenuItem: action,
            handleClickGravarAlteracaoRemessa: action,
            carregarOptionsRemessaJulgamento: computed
        });

        runInAction(() => {
            if (this.observableProps.registro?.remessaJulgamento?.tipo?.id) {
                this.novoTipoRemessa = this.observableProps.registro.remessaJulgamento.tipo.id;
            } else if (this.observableProps.tipoRemessaPadrao) {
                this.novoTipoRemessa = this.observableProps.tipoRemessaPadrao.codigo;
            }

            if (this.observableProps.registro?.remessaJulgamento?.descricaoMotivo) {
                this.novoMotivoRemessa = this.observableProps.registro.remessaJulgamento.descricaoMotivo;
            } else {
                this.definirMotivoPadrao();
            }

            if (this.observableProps.registro?.remessaJulgamento?.complemento) {
                this.novoComplementoMotivoRemessa = this.observableProps.registro.remessaJulgamento.complemento;
            }
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get idTipoRemessaJulgamento() {
        return this.novoTipoRemessa || this.registro?.remessaJulgamento?.tipo?.id || '';
    }

    get motivoRemessaJulgamento() {
        if (typeof this.novoMotivoRemessa !== 'undefined') {
            return this.novoMotivoRemessa;
        }

        return this.registro?.remessaJulgamento?.descricaoMotivo || '';
    }

    get complementoMotivoRemessaJulgamento() {
        if (typeof this.novoComplementoMotivoRemessa !== 'undefined') {
            return this.novoComplementoMotivoRemessa;
        }

        return this.registro?.remessaJulgamento?.complemento || '';
    }

    get sessaoSemPeriodoVirtualDefinido() {
        return (
            !this.sessaoSelecionada?.prazosPorSessao ||
            !this.sessaoIdAssociada ||
            !this.sessaoSelecionada.prazosPorSessao[this.sessaoIdAssociada]?.temPrazoDeVotacaoVirtual
        );
    }

    get sessaoIdAssociada() {
        if (this.registro?.sessaoId) {
            return this.registro.sessaoId;
        }

        // Para processos disponíveis, não há sessão cadastrada no processo, utilizar a selecionada na combo de pauta
        return this.pautaJulgamentoStore?.idPautaSelecionada;
    }

    get sessaoSelecionada() {
        return this.sessaoStore.sessaoSelecionada;
    }

    get meioJulgamentoSessao() {
        if (this.sessaoSelecionada?.tipoJulgamento) {
            return this.sessaoSelecionada?.tipoJulgamento;
        }

        return '';
    }

    get pautaExclusivamenteVirtual() {
        return this.meioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo;
    }

    get motivoEditavel() {
        return false; // TODO Permitir que o usuário altere o motivo da remessa
    }

    get complementoMotivoEditavel() {
        return true; // TODO Verificar se há motivos para não editar o complemento do motivo da remessa
    }

    get isNovoMotivoPreenchido() {
        return typeof this.novoMotivoRemessa === 'string' && this.novoMotivoRemessa.trim().length > 0;
    }

    get isNovoComplementoMotivoPreenchido() {
        return (
            typeof this.novoComplementoMotivoRemessa === 'string' && this.novoComplementoMotivoRemessa.trim().length > 0
        );
    }

    get isDadosNovosIguaisAosAntigos() {
        return (
            this.registro?.remessaJulgamento?.tipo?.id === this.novoTipoRemessa &&
            this.registro?.remessaJulgamento?.descricaoMotivo === this.novoMotivoRemessa &&
            this.registro?.remessaJulgamento?.complemento === this.novoComplementoMotivoRemessa
        );
    }

    get isCamposObrigatoriosPreenchidos() {
        return !this.msgDadosObrigatorios;
    }

    get isCamposConsistentes() {
        return !this.msgDadosInconsistentes;
    }

    get msgDadosObrigatorios() {
        let camposNaoInformados = '';

        if (!this.novoTipoRemessa) {
            camposNaoInformados += 'tipo da remessa, ';
        }

        if (!this.isNovoMotivoPreenchido) {
            camposNaoInformados += 'motivo da remessa, ';
        }

        if (!this.isNovoComplementoMotivoPreenchido) {
            camposNaoInformados += 'especificação (complemento) do motivo da remessa, ';
        }

        const ultimaVirgula = camposNaoInformados.lastIndexOf(', ');
        return camposNaoInformados ? `Informe: ${camposNaoInformados.slice(0, ultimaVirgula)}.` : '';
    }

    get msgDadosInconsistentes() {
        let camposInconsistentes = '';

        if (this.novoTipoRemessa && !this.isTipoRemessaCompativel(this.novoTipoRemessa, this.meioJulgamentoSessao)) {
            camposInconsistentes += 'tipo da remessa é incompatível com o meio de julgamento da sessão, ';
        }

        const ultimaVirgula = camposInconsistentes.lastIndexOf(', ');
        return camposInconsistentes ? `Dados inválidos: ${camposInconsistentes.slice(0, ultimaVirgula)}.` : '';
    }

    get dicaBotaoGravar() {
        if (this.isCamposObrigatoriosPreenchidos && this.isCamposConsistentes) {
            return this.isDadosNovosIguaisAosAntigos ? 'Dados idênticos aos anteriores!' : 'Gravar dados';
        }

        return this.msgDadosObrigatorios || this.msgDadosInconsistentes || '';
    }

    get isFormOk() {
        return !this.isDadosNovosIguaisAosAntigos && !this.msgDadosObrigatorios && !this.msgDadosInconsistentes;
    }

    handleChangeTipoRemessaJulgamento = (event) => {
        this.novoTipoRemessa = event.target.value;

        // TODO Permitir que o usuário altere o motivo da remessa
        if (this.novoTipoRemessa) {
            this.definirMotivoPadrao();
        }
    };

    handleChangeMotivoRemessaJulgamento = (event) => {
        this.novoMotivoRemessa = event.target.value;
    };

    handleChangeComplementoMotivoRemessaJulgamento = (event) => {
        this.novoComplementoMotivoRemessa = event.target.value;

        // TODO Permitir que o usuário altere o motivo da remessa
        if (this.novoComplementoMotivoRemessa) {
            this.definirMotivoPadrao();
        }
    };

    definirMotivoPadrao = () => {
        this.novoMotivoRemessa = MOTIVO_PADRAO;
    };

    handleCloseConfirmacaoExclusao = () => {
        this.exclusaoConfirmada = false;
        this.openConfirmacaoExclusaoRemessa = false;
    };

    handleClickConfirmacaoExclusao = async () => {
        this.exclusaoConfirmada = true;
        this.openConfirmacaoExclusaoRemessa = false;
        await this.salvarRemessa();
        this.handleCloseMenuItem();
    };

    async salvarRemessa() {
        let remessaGravada;

        try {
            runInAction(() => {
                this.executandoAcao = true;
            });

            if (this.novoTipoRemessa === TIPO_REMESSA_JULGAMENTO_PROCESSO.NR.codigo) {
                console.log('Excluindo remessa...');
                // TODO Migrar para um serviço específico de exclusão: await this.processoService.excluirRemessa(this.registro.sessaoId, this.registro.processo.numeroCompleto);

                remessaGravada = await this.processoService.remeterParaSessaoFutura(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    '',
                    ''
                );

                runInAction(() => {
                    this.registro.remessaJulgamento = undefined;
                });
            } else if (this.pautaExclusivamenteVirtual) {
                remessaGravada = await this.processoService.remeterParaSessaoFutura(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    this.novoTipoRemessa,
                    this.novoComplementoMotivoRemessa
                );

                runInAction(() => {
                    if (remessaGravada?.tipo?.id) {
                        this.registro.remessaJulgamento = remessaGravada;
                        this.registro.tipoJulgamento = MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo;
                    }
                });
            } else {
                // TODO Migrar para o novo serviço de remessa para presencial quando estiver disponível (o serviço deverá retornar o objeto "remessaJulgamento")
                await this.processoService.remeterParaPresencial(this.registro, this.novoComplementoMotivoRemessa);

                runInAction(() => {
                    this.registro.tipoJulgamento = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
                });
            }

            this.mensagemStore.abrirMensagemSucesso(`Remessa gravada com sucesso.`);
        } catch (error) {
            console.error(error);
            this.mensagemStore.abrirMensagemErro(`Falha ao tentar gravar os dados da remessa.`);
        } finally {
            runInAction(() => {
                this.executandoAcao = false;
            });
        }
    }

    handleCloseMenuItem = () => {
        this.novoTipoRemessa = '';
        this.novoMotivoRemessa = '';
        this.novoComplementoMotivoRemessa = '';
        this.observableProps.onClose();
    };

    renderDialogConfirmacaoExclusao() {
        let msgTitulo = 'Confirmação de exclusão da remessa';
        let msgAlerta =
            'Atenção: A remessa será excluída e o processo voltará para a sessão virtual. Deseja confirmar a exclusão?';

        return (
            <Dialog
                open={this.openConfirmacaoExclusaoRemessa}
                onClose={this.handleCloseConfirmacaoExclusao}
                maxWidth="xs"
            >
                <DialogTitle>
                    <Typography>{msgTitulo}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack direction="row">
                        <Warning sx={{ fill: 'darkorange' }} />
                        <DialogContentText
                            sx={{ ml: 2 }}
                            id="alert-dialog-description-exclusao-remessa-julgamento"
                        >
                            {msgAlerta}
                        </DialogContentText>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ mr: 2, mb: 1 }}>
                    <Button
                        color="secondary"
                        variant="outlined"
                        endIcon={<CancelIcon />}
                        onClick={this.handleCloseConfirmacaoExclusao}
                    >
                        Cancelar
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        endIcon={<DeleteForeverIcon />}
                        sx={{ ml: 2 }}
                        onClick={this.handleClickConfirmacaoExclusao}
                        loading={this.executandoAcao}
                        loadingPosition="end"
                    >
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    handleClickGravarAlteracaoRemessa = async () => {
        if (this.novoTipoRemessa === TIPO_REMESSA_JULGAMENTO_PROCESSO.NR.codigo) {
            if (this.exclusaoConfirmada) {
                console.log(`Excluindo remessa... [${this.registro.processo.numeroCompleto}]`);
                await this.salvarRemessa();
            } else {
                this.openConfirmacaoExclusaoRemessa = true;
            }
        } else {
            console.log(`Salvando novos dados da remessa... [${this.registro.processo.numeroCompleto}]`);
            await this.salvarRemessa();
        }

        this.handleCloseMenuItem();
    };

    isTipoRemessaCompativel(codigoTipoRemessa, codigoMeioJulgamentoSessao) {
        const tiposRemessaVirtual = new Set([
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RF.codigo,
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RFP.codigo,
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RFV.codigo
        ]);

        if (codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo) {
            return codigoTipoRemessa !== TIPO_REMESSA_JULGAMENTO_PROCESSO.RP.codigo;
        } else {
            return !tiposRemessaVirtual.has(codigoTipoRemessa);
        }
    }

    get carregarOptionsRemessaJulgamento() {
        const tiposRemessa = Object.values(TIPO_REMESSA_JULGAMENTO_PROCESSO).filter(
            (tipoRemessa) => tipoRemessa.codigo !== TIPO_REMESSA_JULGAMENTO_PROCESSO.NR.codigo
        );

        return tiposRemessa.map(
            (tipoRemessa) =>
                tipoRemessa?.id && (
                    <FormControlLabel
                        value={tipoRemessa.codigo}
                        control={<Radio />}
                        key={tipoRemessa.codigo}
                        label={
                            <Stack
                                direction="row"
                                sx={{ alignItems: 'center', alignContent: 'center' }}
                            >
                                {TipoRemessaJulgamentoProcesso.getIcone(tipoRemessa.id)}
                                <Typography sx={{ ml: 1 }}>{tipoRemessa.descricao}</Typography>
                            </Stack>
                        }
                        title={tipoRemessa.descricao}
                        disabled={!this.isTipoRemessaCompativel(tipoRemessa.codigo, this.meioJulgamentoSessao)}
                    />
                )
        );
    }

    render() {
        return (
            <Box sx={{ m: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        variant="h6"
                        color="primary"
                        sx={{ m: 1 }}
                    >
                        Tipo de remessa
                    </Typography>
                    {this.registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.id &&
                        this.sessaoSemPeriodoVirtualDefinido && (
                            <Tooltip
                                placement={'top'}
                                title={
                                    <Typography borderColor={'darkorange'}>
                                        {MENSAGEM_INCOSISTENCIA.VIRTUAL_EM_SESSAO_SEM_PERIODO_VIRTUAL}
                                    </Typography>
                                }
                            >
                                <Warning sx={{ color: 'darkorange' }} />
                            </Tooltip>
                        )}
                </Box>
                <FormControl>
                    <RadioGroup
                        name="tipoRemessaJulgamento"
                        value={this.idTipoRemessaJulgamento}
                        onChange={this.handleChangeTipoRemessaJulgamento}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            ml: 2,
                            justifyContent: 'space-between'
                        }}
                    >
                        {this.carregarOptionsRemessaJulgamento}
                    </RadioGroup>
                </FormControl>
                <Typography
                    variant="h6"
                    color="primary"
                    sx={{ ml: 1, mt: 2, mb: 1 }}
                >
                    Motivo da remessa
                </Typography>
                <Box
                    component="form"
                    sx={{
                        '& > :not(style)': { width: '100%' }
                    }}
                    noValidate
                    autoComplete="off"
                >
                    <TextField
                        required
                        id="motivo-remessa"
                        variant="outlined"
                        value={this.motivoRemessaJulgamento}
                        onChange={this.handleChangeMotivoRemessaJulgamento}
                        maxRows={1}
                        slotProps={{
                            htmlInput: { maxLength: QTD_MAXIMA_CARACTERES_MOTIVO_REMESSA }
                        }}
                        disabled={!this.motivoEditavel}
                    />
                </Box>
                <Typography
                    variant="h6"
                    color="primary"
                    sx={{ ml: 1, mt: 2, mb: 1 }}
                >
                    Especificação do motivo da remessa
                </Typography>
                <Box
                    component="form"
                    sx={{
                        '& > :not(style)': { width: '100%' }
                    }}
                    noValidate
                    autoComplete="off"
                >
                    <TextField
                        required
                        id="complemento-motivo-remessa"
                        variant="outlined"
                        value={this.complementoMotivoRemessaJulgamento}
                        onChange={this.handleChangeComplementoMotivoRemessaJulgamento}
                        multiline
                        maxRows={5}
                        slotProps={{
                            htmlInput: { maxLength: QTD_MAXIMA_CARACTERES_MOTIVO_REMESSA }
                        }}
                        disabled={!this.complementoMotivoEditavel}
                    />
                    <Box sx={{ mt: 1, mr: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography fontSize={'small'}>
                            {`${this.complementoMotivoRemessaJulgamento.length} / ${QTD_MAXIMA_CARACTERES_MOTIVO_REMESSA}`}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Button
                        color="secondary"
                        variant="outlined"
                        size="small"
                        endIcon={<CancelIcon fontSize="medium" />}
                        onClick={this.handleCloseMenuItem}
                    >
                        Cancelar
                    </Button>
                    <Tooltip title={<Typography className="tooltipBotao">{this.dicaBotaoGravar}</Typography>}>
                        <span>
                            <Button
                                color="primary"
                                variant="contained"
                                size="small"
                                endIcon={<SaveIcon fontSize="medium" />}
                                sx={{ ml: 2 }}
                                disabled={!this.isFormOk}
                                onClick={this.handleClickGravarAlteracaoRemessa}
                                loading={this.executandoAcao}
                                loadingPosition="end"
                            >
                                Salvar
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        );
    }
}

export default inject('stores')(observer(IconeTipoRemessaJulgamentoFormRemessa));
